/**
 * Runtime checks for the hex GeoJSON files.
 *
 * `response.json()` is untyped. These schemas check the envelope and the
 * properties the map reads, and leave polygon coordinates to MapLibre.
 * The color scales stay `unknown` so the lenient scale readers can drop a
 * bad stop without rejecting the file.
 */

import type { Polygon } from 'geojson';
import { z } from 'zod';

const polygon = z.custom<Polygon>(
  (value) =>
    typeof value === 'object' &&
    value !== null &&
    (value as { type?: unknown }).type === 'Polygon',
);

export const hexPriceProperties = z.object({
  meanPrice: z.number().nullable(),
  numHouses: z.number(),
  meanPricePerSqft: z.number().nullable(),
  numSqftSales: z.number(),
});

export const hexPricePopup = hexPriceProperties.pick({
  meanPrice: true,
  numHouses: true,
});

export const hexSqftPopup = hexPriceProperties.pick({
  meanPricePerSqft: true,
  numSqftSales: true,
});

const feature = <T extends z.ZodType>(properties: T) =>
  z.object({
    type: z.literal('Feature'),
    geometry: polygon,
    properties,
  });

export const meanPriceCollection = z.object({
  type: z.literal('FeatureCollection'),
  features: z.array(feature(hexPriceProperties)),
  meanPriceScale: z.unknown().optional(),
  meanPricePerSqftScale: z.unknown().optional(),
});

export type MeanPriceCollection = z.infer<typeof meanPriceCollection>;

export const priceChangeProperties = z.object({
  annualizedPct: z.number().nullable(),
  numHalves: z.number(),
  numSales: z.number(),
  annualizedPctPerSqft: z.number().nullable(),
  numHalvesPerSqft: z.number(),
  numSalesPerSqft: z.number(),
});

export const priceChangePopup = priceChangeProperties.pick({
  annualizedPct: true,
  numHalves: true,
  numSales: true,
});

export const priceChangeSqftPopup = priceChangeProperties.pick({
  annualizedPctPerSqft: true,
  numHalvesPerSqft: true,
  numSalesPerSqft: true,
});

export const priceChangeCollection = z.object({
  type: z.literal('FeatureCollection'),
  features: z.array(feature(priceChangeProperties)),
  priceChangeScale: z.unknown().optional(),
  priceChangePerSqftScale: z.unknown().optional(),
});

export type PriceChangeCollection = z.infer<typeof priceChangeCollection>;
